import React from 'react';
import cn from 'bem-cn';

import { useIsHydrated } from '../../hooks';
import { FeedCardData } from '../types';
import { PromptCard } from '../PromptCard';
import { CourseCard } from '../CourseCard/CourseCard';
import { ChallengeCard } from '../ChallengeCard/ChallengeCard';
import { PromptCardSkeleton } from '../PromptCardSkeleton';
import { NewPromptCard } from '../NewPromptCard';
import { useFeed } from './Feed.hooks';
import { Entity, FeedProps } from './Feed.types';

import './Feed.scss';

const b = cn('feed');

export const Feed: React.FC<FeedProps> = ({
    prompts = [],
    challenges = [],
    itemsSize = 'normal',
    courses = [],
    className,
    containerClassName,
    columns = 2,
    withHover = true,
    isLoading = false,
    hasMore = true,
    showStatuses = false,
    showNewPromptCard = false,
}) => {
    const isLoaded = useIsHydrated();

    const displayItems = useFeed(
        {
            prompts,
            challenges,
            courses,
        },
        columns,
    );

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderPrompt = (item: any) => (
        <PromptCard
            withHover={withHover}
            key={`prompt-${item.id}`}
            prompt={item}
            size={itemsSize}
            showStatus={showStatuses}
        />
    );

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderChallenge = (item: any) => (
        <ChallengeCard
            className={b('item', { 'type-challenge': true })}
            key={`challenge-${item.id}`}
            challenge={item}
            alwaysOn
        />
    );

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderCourse = (item: any) => (
        <CourseCard className={b('item', { 'type-course': true })} key={`course-${item.id}`} course={item} />
    );

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderItem = (item: any) => {
        switch (item.type) {
            case Entity.Prompt:
                return renderPrompt(item);
            case Entity.Challenge:
                return renderChallenge(item);
            case Entity.Course:
                return renderCourse(item);
            default:
                return renderPrompt(item);
        }
    };

    const renderSkeletonCards = (count: number = 4) => {
        return Array.from({ length: count }, (_, index) => (
            <PromptCardSkeleton size={itemsSize} key={`skeleton-${index}`} />
        ));
    };

    return (
        <div className={containerClassName}>
            <div
                className={b({ loaded: isLoaded, isItemSmall: itemsSize === 'small' }).mix(className)}
                style={{ '--columns-count': columns } as React.CSSProperties}
            >
                {displayItems?.map((item: FeedCardData) => renderItem(item))}
                {showNewPromptCard && <NewPromptCard />}
                {isLoading && renderSkeletonCards(itemsSize === 'small' ? columns : columns * 3)}
            </div>
            {!hasMore && prompts.length === 0 && <div className={b('no-results')}>Ничего не нашлось</div>}
        </div>
    );
};
